import styled from "styled-components";
import { Tooltip } from "antd";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";

// #region STYLES
const TooltipContent = styled.div`
    background: ${({ $backgroundColor }) =>
        $backgroundColor || "var(--backgrounds-lines-n-600, #10141b)"};
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    line-height: 14px;
    padding: 0 4px;
    text-align: left;
`;

const TooltipTitle = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 700;
`;

const TooltipBody = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 400;
`;

const StyledInfoCircle = styled(InfoCircle)`
    align-self: center;
    color: ${({ $enabled }) =>
        $enabled ? "var(--text-icons-gray-100, #d1d1d1)" : "var(--text-icons-gray-300, #4f5b6d)"};
    cursor: help;
    flex-shrink: 0;
    height: ${({ $infoIconSize }) => ($infoIconSize ? $infoIconSize : "16px")};
    margin-right: 4px;
    overflow: visible;
    position: relative;
    top: 1px;
    width: ${({ $infoIconSize }) => ($infoIconSize ? $infoIconSize : "16px")};
`;
// #endregion

// #region COMPONENTS

/**
 * TooltipWithInfoCircle component
 * @param {object} props
 * @param {boolean} props.enabled - If true, tooltip will be shown
 * @param {boolean} props.open - If true, tooltip will be shown
 * @param {string} props.header - The header of the tooltip
 * @param {React.ReactNode} props.title - The body of the tooltip
 * @param {string} props.width - The width of the tooltip
 * @param {string} props.placement - The tooltip placement relative to the info icon
 * @param {string} props.backgroundColor - Optional tooltip background color
 * @param {string} props.iconSize - The size of the info icon
 * @param {string} props.iconClassName - The class name of the info icon
 * @returns {React.ReactNode} - The tooltip
 */
export const TooltipWithInfoCircle = ({
    enabled = false,
    open,
    header,
    title,
    width = "250px",
    placement = "top",
    backgroundColor,
    iconSize = "16px",
    iconClassName
}) => {
    const usesSidePlacement =
        placement?.toLowerCase().startsWith("left") || placement?.toLowerCase().startsWith("right");
    const hasTitle = header !== null && header !== undefined && header !== "";
    const hasBody = title !== null && title !== undefined && title !== "";
    const tooltipTitle =
        hasTitle || hasBody ? (
            <TooltipContent $backgroundColor={backgroundColor}>
                {hasTitle && <TooltipTitle>{header}</TooltipTitle>}
                {hasBody && <TooltipBody>{title}</TooltipBody>}
            </TooltipContent>
        ) : null;

    return (
        <Tooltip
            open={open}
            color={backgroundColor || "#10141b"}
            placement={placement}
            overlayStyle={usesSidePlacement ? { maxWidth: width, width } : undefined}
            overlayInnerStyle={{
                textAlign: "left",
                width: usesSidePlacement ? "100%" : width,
                height: "100%",
                ...(usesSidePlacement ? {} : { left: "20%", transform: "translateX(-20%)" })
            }}
            title={tooltipTitle}>
            <StyledInfoCircle className={iconClassName} $enabled={enabled} $infoIconSize={iconSize} />
        </Tooltip>
    );
};

export default TooltipWithInfoCircle;
// #endregion
